import { useCallback } from 'react';
import { Note, useCompanyNoteModal } from '@components/Note';
import type { CompanyNote, CompanyNotes } from '@components/Note';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { Grid, Heading, useTheme } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { AddButton, NotesSidebarList, SidebarHeader } from './styled';

interface Props {
  closeSidebar: () => void;
  notes: CompanyNotes;
  companyId?: string;
  uniqueSymbol?: string;
}

const NotesSidebar = ({
  closeSidebar,
  notes,
  companyId,
  uniqueSymbol,
}: Props) => {
  const theme = useTheme();
  const { openNoteModal } = useCompanyNoteModal();
  const canCreateNote = !!companyId && !!uniqueSymbol;

  const handleAddNote = useCallback(() => {
    if (!canCreateNote) {
      return;
    }
    openNoteModal({ companyId, uniqueSymbol });
  }, [companyId, openNoteModal]);

  const handleEditNote = useCallback(
    (note: CompanyNote) => {
      if (!canCreateNote) {
        return;
      }
      openNoteModal({ note, companyId, uniqueSymbol });
    },
    [openNoteModal, companyId, uniqueSymbol]
  );

  const { trackEvent } = useTracking();
  const iconSize = theme.x1spacePx * 2.5; // 20px
  return (
    <NotesSidebarList data-cy-id="notes-sidemenu">
      <SidebarHeader
        templateColumns="1fr auto auto"
        gap={`${theme.x1spacePx}px`}
        justifyContent="start"
        alignItems="center"
      >
        <Heading as="h3">Notes</Heading>
        <AddButton
          data-cy-id="notes-sidemenu-add"
          styleType="gentleReverse"
          onClick={() => {
            trackEvent({
              action: 'click',
              modifier: 'add-note',
              subject: 'my-notes-side-menu',
            });
            handleAddNote();
          }}
        >
          <PlusSolid />
          &nbsp;Add
        </AddButton>
        <button data-cy-id="notes-sidemenu-close" onClick={closeSidebar}>
          <CrossSolid
            height={iconSize}
            width={iconSize}
            fill="white"
            opacity={0.5}
          />
        </button>
      </SidebarHeader>
      <Grid gap={`${theme.x1spacePx}px`}>
        {notes.map((n, i) => (
          <Note
            data-cy-id={`note-side-menu-${i}`}
            key={`note-side-menu-${n.id}`}
            note={n}
            editNote={handleEditNote}
          />
        ))}
      </Grid>
    </NotesSidebarList>
  );
};

export default NotesSidebar;
